// src/pages/Admin/AdminUsers.js
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useAuth } from '../../context/AuthContext'
import { adminAPI } from '../../services/api'
import { API_BASE_URL } from '../../config'
import { mensagemDeErro } from '../../services/erros'

const AdminUsers = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [users, setUsers] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null })
  const [deleting, setDeleting] = useState(false)
  const [editDialog, setEditDialog] = useState({ open: false, user: null })
  const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (user && !user.is_staff) {
      navigate('/')
    }
  }, [user, navigate])

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const params = { page: page + 1 }
      if (search) params.search = search
      if (roleFilter) params.role = roleFilter
      if (statusFilter) params.status = statusFilter
      const response = await adminAPI.getUsers(params)
      setUsers(response.data.results || [])
      setTotal(response.data.count || 0)
    } catch {
      setError('Erro ao carregar usuários.')
    } finally {
      setLoading(false)
    }
  }, [page, search, roleFilter, statusFilter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setPage(0)
  }

  const handleRoleChange = (e) => {
    setRoleFilter(e.target.value)
    setPage(0)
  }

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value)
    setPage(0)
  }

  const handleOpenEdit = (targetUser) => {
    setEditData({
      is_active: targetUser.is_active,
      is_verified: targetUser.is_verified,
      is_staff: targetUser.is_staff,
      is_realtor: targetUser.is_realtor,
      is_agency: targetUser.is_agency,
    })
    setEditDialog({ open: true, user: targetUser })
  }

  const handleSaveEdit = async () => {
    if (!editDialog.user) return
    try {
      setSaving(true)
      setError('')
      await adminAPI.updateUser(editDialog.user.id, editData)
      setEditDialog({ open: false, user: null })
      fetchUsers()
    } catch (err) {
      const msg = mensagemDeErro(err, 'Erro ao atualizar usuário.')
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteDialog.user) return
    try {
      setDeleting(true)
      setError('')
      await adminAPI.deleteUser(deleteDialog.user.id)
      setDeleteDialog({ open: false, user: null })
      fetchUsers()
    } catch (err) {
      const msg = mensagemDeErro(err, 'Erro ao excluir usuário.')
      setError(msg)
    } finally {
      setDeleting(false)
    }
  }

  const getUserRole = (u) => {
    if (u.is_agency) return { label: 'Imobiliária', color: 'secondary' }
    if (u.is_realtor) return { label: 'Corretor', color: 'info' }
    return { label: 'Regular', color: 'default' }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <PeopleIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">
          Gerenciar Usuários
        </Typography>
        <Chip label={total} color="primary" size="small" sx={{ ml: 1 }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Buscar por nome, email, telefone..."
          value={search}
          onChange={handleSearchChange}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Tipo</InputLabel>
          <Select value={roleFilter} onChange={handleRoleChange} label="Tipo">
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="realtor">Corretor</MenuItem>
            <MenuItem value="agency">Imobiliária</MenuItem>
            <MenuItem value="regular">Regular</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={handleStatusChange} label="Status">
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="active">Ativo</MenuItem>
            <MenuItem value="inactive">Inativo</MenuItem>
            <MenuItem value="verified">Verificado</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Usuário</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Imóveis</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cadastro</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nenhum usuário encontrado.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => {
                  const role = getUserRole(u)
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={u.avatar ? `${API_BASE_URL}${u.avatar}` : undefined}
                            sx={{ width: 40, height: 40 }}
                          >
                            {u.first_name?.[0] || u.email?.[0] || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                              {u.first_name} {u.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {u.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={role.label} color={role.color} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={u.is_active ? 'Ativo' : 'Inativo'}
                          color={u.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{u.properties_count}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(u.date_joined).toLocaleDateString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenEdit(u)}
                          >
                            Editar
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteDialog({ open: true, user: u })}
                            disabled={u.id === user?.id}
                          >
                            Excluir
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={20}
          rowsPerPageOptions={[20]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>

      {/* Dialog de edição de permissões */}
      <Dialog
        open={editDialog.open}
        onClose={() => !saving && setEditDialog({ open: false, user: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Editar Permissões
          {editDialog.user && (
            <Typography variant="body2" color="text.secondary">
              {editDialog.user.first_name} {editDialog.user.last_name} ({editDialog.user.email})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editData.is_active || false}
                  onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                />
              }
              label="Ativo"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editData.is_verified || false}
                  onChange={(e) => setEditData({ ...editData, is_verified: e.target.checked })}
                />
              }
              label="Verificado"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editData.is_staff || false}
                  onChange={(e) => setEditData({ ...editData, is_staff: e.target.checked })}
                />
              }
              label="Administrador"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editData.is_realtor || false}
                  onChange={(e) => setEditData({ ...editData, is_realtor: e.target.checked })}
                />
              }
              label="Corretor"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editData.is_agency || false}
                  onChange={(e) => setEditData({ ...editData, is_agency: e.target.checked })}
                />
              }
              label="Imobiliária"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialog({ open: false, user: null })}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, user: null })}
        maxWidth="xs"
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja excluir o usuário{' '}
            <strong>"{deleteDialog.user?.first_name} {deleteDialog.user?.last_name}"</strong> ({deleteDialog.user?.email})?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita e remove todos os dados associados (imóveis, avaliações, impulsionamentos, pagamentos).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, user: null })}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default AdminUsers
